import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

interface PriceSparklineProps {
  points: { price: number; recorded_at: string }[];
  changeLabel?: string | null;
  changePercent?: number | null;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs">
      <p className="text-gray-500 mb-0.5">{label}</p>
      <p className="font-semibold text-gray-900">${(payload[0].value as number).toFixed(2)}</p>
    </div>
  );
};

const PriceSparkline: React.FC<PriceSparklineProps> = ({ points, changeLabel, changePercent }) => {
  if (points.length === 0) {
    return <p className="text-xs text-gray-400 italic">No price data yet</p>;
  }

  if (points.length === 1) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">${points[0].price.toFixed(2)}</span>
        <p className="text-xs text-gray-400 italic">Recording price history…</p>
      </div>
    );
  }

  const data = points.map(p => ({
    date: fmtDate(p.recorded_at),
    price: p.price,
  }));

  const prices = points.map(p => p.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const pad = (maxP - minP) * 0.2 || 1;
  const yMin = Math.max(0, minP - pad);
  const yMax = maxP + pad;

  let changeLabelClass = 'text-gray-500';
  if (changePercent != null) {
    changeLabelClass = changePercent < 0 ? 'text-green-600' : changePercent > 0 ? 'text-red-500' : 'text-gray-500';
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={120}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.12} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `$${v.toFixed(0)}`}
            width={38}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="price"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#priceGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#3b82f6', strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
      {changeLabel != null && (
        <p className={`text-xs mt-1 font-medium ${changeLabelClass}`}>{changeLabel}</p>
      )}
    </div>
  );
};

export default PriceSparkline;
